import React, { useEffect, useMemo } from 'react';
import MessageIcon from '@mui/icons-material/Message';
import IconButton from '@mui/material/IconButton';
import useDialogState from '../hooks/useDialogState';
import { primary } from '@/theme/colors';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { selectedRiskAssessmentFilters } from '../../state';
import { useCommentsState } from '../hooks/useCommentsState';
import { useAtomValue } from 'jotai';

interface OpenDialogButtonProps {
  identifer: string;
  title: string;
  location: string;
}

const OpenDialogButton: React.FC<OpenDialogButtonProps> = ({ identifer, title, location }) => {
  const { handleOpenDialog } = useDialogState();
  const { commentState: comments, setCommentsState: setComments } = useCommentsState(location);

  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const structureId = useGetStructureId();
  const commentInitialized = useMemo(
    () => comments?.comments.some((comment) => comment.key === identifer),
    [comments?.comments, identifer]
  );
  const commentFilled = useMemo(
    () => comments?.comments.some((comment) => comment.key === identifer && comment.text !== ''),
    [comments?.comments, identifer]
  );

  useEffect(() => {
    if (commentInitialized) {
      return;
    }
    const newComment = {
      key: identifer,
      text: '',
      type: location,
      title,
    };
    setComments((previous) => {
      if (!previous)
        return {
          structureId,
          collectionName: 'assembly',
          selectedFilters,
          comments: [newComment],
          updateAllAssemblies: false,
        };
      return {
        ...previous,
        comments: [...previous.comments, newComment],
      };
    });
  }, [commentInitialized, identifer, location, selectedFilters, setComments, structureId, title]);

  useEffect(() => {
    return () => {
      setComments((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          comments: previous.comments.filter((comment) => !(comment.key === identifer)),
        };
      });
    };
  }, [identifer, setComments]);

  return (
    <IconButton disableRipple sx={{ height: 'fit-content', padding: '4px' }}>
      <MessageIcon
        sx={{ color: commentFilled ? primary.LightBlue : '#c5c5c5' }}
        onClick={() => handleOpenDialog(true, identifer, title)}
      />
    </IconButton>
  );
};

export default OpenDialogButton;
